import type { EquivalenciasUmDetalle, UmEquivalenciaDto } from '../services/productoService';

export type ContextoUmVenta = 'pedido' | 'factura' | 'base' | 'remito';

const normUm = (um: string | null | undefined): string => String(um ?? '').trim();

const umKey = (um: string): string => normUm(um).toUpperCase();

/**
 * UM del renglón desde el detalle de la transacción (GET Pedido/Factura/Remito/…).
 * Preferir siempre esto al editar/ver/convertir; no usar logística del artículo si ya viene.
 */
export function umDesdeDetalleTransaccion(detalle: unknown): string {
  const o = (detalle ?? {}) as Record<string, unknown>;
  return String(o.um ?? o.Um ?? o.UM ?? '').trim();
}

/** Lee propiedad string ignorando mayúsculas/minúsculas. */
function readUmProp(obj: Record<string, unknown>, ...candidates: string[]): string {
  const lowerToReal = new Map<string, string>();
  for (const k of Object.keys(obj)) {
    lowerToReal.set(k.toLowerCase(), k);
  }
  for (const c of candidates) {
    const real = lowerToReal.get(c.toLowerCase());
    if (!real) continue;
    const raw = obj[real];
    if (raw == null) continue;
    if (typeof raw === 'object') {
      const nested = raw as Record<string, unknown>;
      const id =
        normUm(nested.idUnidadMedida as string) ||
        normUm(nested.IdUnidadMedida as string) ||
        normUm(nested.codigo as string) ||
        normUm(nested.descripcion as string);
      if (id) return id;
      continue;
    }
    const v = normUm(raw as string);
    if (v) return v;
  }
  return '';
}

/** UM base del artículo (tab Stock). */
export function umBaseProducto(producto: unknown): string {
  const p = (producto ?? {}) as Record<string, unknown>;
  return (
    readUmProp(p, 'idUnidadMedida', 'IdUnidadMedida') ||
    readUmProp(p, 'um', 'Um', 'UM') ||
    readUmProp(p, 'unidadMedida', 'UnidadMedida')
  );
}

/**
 * UM del documento según Logística (uso residual, p. ej. remito sin UM de línea).
 * - pedido → umrefPed; si falta, UM comercial (factura) antes que stock/base
 * - factura → umrefFac (fallback stock / base)
 *
 * El alta de un pedido no usa esta cadena: ver `resolverUmYPrecioDocumento`
 * (UM Ref. Pedido, o la unidad base si está vacía).
 * El alta de una factura o nota de crédito tampoco: UM Ref. Facturación, o la base.
 * `remito` conserva umrefFac → equivalencias → stock/base (remitos y notas de débito).
 */
export function umParaDocumento(producto: unknown, contexto: ContextoUmVenta): string {
  const p = (producto ?? {}) as Record<string, unknown>;
  const base = umBaseProducto(producto);
  if (contexto === 'pedido') {
    return (
      readUmProp(
        p,
        'umrefPed',
        'UmrefPed',
        'umRefPed',
        'UmRefPed',
        'umrefPedido',
        'UmrefPedido',
        'umRefPedido',
      ) ||
      // Comercial antes que stock: evita quedar en UNI cuando solo cargaron PAQ en factura
      readUmProp(p, 'umrefFac', 'UmrefFac', 'umRefFac', 'UmRefFac', 'umrefFactura') ||
      readUmProp(p, 'umrefStock', 'UmrefStock', 'umRefStock', 'UmRefStock') ||
      base
    );
  }
  if (contexto === 'factura' || contexto === 'remito') {
    return (
      readUmProp(
        p,
        'umrefFac',
        'UmrefFac',
        'umRefFac',
        'UmRefFac',
        'umrefFactura',
        'UmrefFactura',
      ) ||
      readUmProp(p, 'umrefStock', 'UmrefStock', 'umRefStock') ||
      base
    );
  }
  return base;
}

type Edge = { to: string; factor: number };

function buildGraph(eqs: UmEquivalenciaDto[]): Map<string, Edge[]> {
  const g = new Map<string, Edge[]>();
  const add = (from: string, to: string, factor: number) => {
    if (!from || !to || !(factor > 0) || !Number.isFinite(factor)) return;
    const list = g.get(from) ?? [];
    list.push({ to, factor });
    g.set(from, list);
  };
  for (const e of eqs) {
    const o = umKey(e.umOrig);
    const d = umKey(e.umDest);
    const n = Number(e.cantDest);
    if (!o || !d || !(n > 0)) continue;
    // 1 orig = n dest
    add(o, d, n);
    add(d, o, 1 / n);
  }
  return g;
}

/**
 * Factor para convertir cantidad: qtyTo = qtyFrom * factor.
 * Convención equivalencias: 1 UM origen = cantDest UM destino.
 * Si no hay camino, null.
 */
export function factorCantidadEntreUm(
  umFrom: string,
  umTo: string,
  eqs: UmEquivalenciaDto[],
  umBase?: string,
): number | null {
  const a = umKey(umFrom);
  const b = umKey(umTo);
  if (!a || !b) return null;
  if (a === b) return 1;

  const graph = buildGraph(eqs);
  const base = umBase ? umKey(umBase) : '';

  const bfs = (start: string, goal: string): number | null => {
    if (start === goal) return 1;
    const visited = new Set<string>([start]);
    const queue: Array<{ um: string; factor: number }> = [{ um: start, factor: 1 }];
    while (queue.length) {
      const cur = queue.shift()!;
      for (const edge of graph.get(cur.um) ?? []) {
        if (visited.has(edge.to)) continue;
        const nextFactor = cur.factor * edge.factor;
        if (edge.to === goal) return nextFactor;
        visited.add(edge.to);
        queue.push({ um: edge.to, factor: nextFactor });
      }
    }
    return null;
  };

  const direct = bfs(a, b);
  if (direct != null) return direct;

  // Puente por UM base si no hay arista directa en el grafo de equivalencias
  if (base && base !== a && base !== b) {
    const aToBase = bfs(a, base);
    const baseToB = bfs(base, b);
    if (aToBase != null && baseToB != null) return aToBase * baseToB;
  }

  return null;
}

/**
 * `precioLista` es el precio de 1 unidad de `umPrecioLista` (si falta, UM base).
 * Devuelve el precio de 1 unidad de `umDocumento`.
 * Sin camino de equivalencia: no inventa factor; devuelve el precio sin convertir.
 */
export function precioEnUmDocumento(
  precioLista: number,
  umDocumento: string,
  umBase: string,
  eqs: UmEquivalenciaDto[],
  umPrecioLista?: string | null,
): number {
  const price = Number(precioLista) || 0;
  const umDoc = normUm(umDocumento);
  const umB = normUm(umBase) || umDoc;
  const umP = normUm(umPrecioLista) || umB;
  if (!umDoc || !umP || umKey(umDoc) === umKey(umP)) return price;

  // Cuántas unidades de umPrecio equivalen a 1 UM documento
  const factor = factorCantidadEntreUm(umDoc, umP, eqs, umB || umP);
  if (factor == null || !Number.isFinite(factor)) return price;
  return price * factor;
}

export function convertirCantidadEntreUm(
  cantidad: number,
  umFrom: string,
  umTo: string,
  eqs: UmEquivalenciaDto[],
  umBase?: string,
): number | null {
  const qty = Number(cantidad);
  if (!Number.isFinite(qty)) return null;
  const factor = factorCantidadEntreUm(umFrom, umTo, eqs, umBase);
  if (factor == null) return null;
  return qty * factor;
}

/**
 * UMs disponibles para chips: UM base + orígenes/destinos de equivalencias.
 * Sin equivalencias → lista vacía (la UI muestra solo el texto de la línea).
 */
export function listarUmsConEquivalencias(
  detalle: EquivalenciasUmDetalle | null | undefined,
  umLineaActual?: string | null,
): string[] {
  const eqs = detalle?.equivalencias ?? [];
  // Sin equivalencias no hay conversión fiable: la UI muestra solo el texto de la línea.
  if (eqs.length === 0) return [];

  const out: string[] = [];
  const push = (u: string | null | undefined) => {
    const n = normUm(u);
    if (!n) return;
    if (out.some((x) => umKey(x) === umKey(n))) return;
    out.push(n);
  };

  push(detalle?.umBase);
  push(detalle?.umrefPed);
  push(detalle?.umrefFac);
  push(detalle?.umrefPrep);
  push(detalle?.umrefStock);
  for (const e of eqs) {
    push(e.umOrig);
    push(e.umDest);
  }
  // Conserva la UM de la transacción aunque no esté en el grafo (p. ej. tipografía distinta).
  push(umLineaActual);

  return out;
}

/** Misma UM sin importar mayúsculas/espacios. */
export function mismaUm(a: string | null | undefined, b: string | null | undefined): boolean {
  const x = normUm(a);
  const y = normUm(b);
  if (!x || !y) return false;
  return umKey(x) === umKey(y);
}

/**
 * Convierte cantidad y precio entre UMs manteniendo el subtotal (precio / factor).
 * Sin camino de equivalencia → null (no inventar factor).
 */
export function convertirCantidadYPrecioEntreUm(
  cantidad: number,
  precioUnitario: number,
  umFrom: string,
  umTo: string,
  eqs: UmEquivalenciaDto[],
  umBase?: string,
): { cantidad: number; precioUnitario: number } | null {
  if (mismaUm(umFrom, umTo)) {
    return { cantidad: Number(cantidad) || 0, precioUnitario: Number(precioUnitario) || 0 };
  }
  const factor = factorCantidadEntreUm(umFrom, umTo, eqs, umBase);
  if (factor == null || !Number.isFinite(factor) || factor === 0) return null;
  const qty = Number(cantidad);
  const precio = Number(precioUnitario);
  if (!Number.isFinite(qty) || !Number.isFinite(precio)) return null;
  return {
    cantidad: qty * factor,
    precioUnitario: precio / factor,
  };
}

export interface LineaUmPrecioResolucion {
  um: string;
  umBase: string;
  precioUnitario: number;
  /** true si se aplicó factor de equivalencia distinto de 1 */
  usoEquivalencia: boolean;
}

/**
 * Solo si la UM documental está vacía y hay equivalencias, elige la UM no-base
 * (ej. base UNI + eq 1 KG=10 UNI → KG). No pisa umrefPed/umrefFac explícitos.
 */
export function umDesdeEquivalenciasSiCorresponde(
  umActual: string,
  umBase: string,
  eqs: UmEquivalenciaDto[],
): string {
  const actual = normUm(umActual);
  if (actual) return actual;

  const base = normUm(umBase);
  for (const e of eqs) {
    const o = normUm(e.umOrig);
    const d = normUm(e.umDest);
    if (!o || !d || umKey(o) === umKey(d)) continue;
    if (base) {
      if (umKey(o) === umKey(base)) return d;
      if (umKey(d) === umKey(base)) return o;
    } else {
      return o;
    }
  }
  return base;
}

/** UM comercial explícita en logística (pedido/factura), sin caer a stock/base. */
function umComercialExplicita(producto: unknown, contexto: ContextoUmVenta): string {
  const p = (producto ?? {}) as Record<string, unknown>;
  if (contexto === 'pedido') {
    // Solo umrefPed: no pisar con umrefFac (si Fac=PAQ y el pedido va en KG por equivalencia).
    return readUmProp(
      p,
      'umrefPed',
      'UmrefPed',
      'umRefPed',
      'UmRefPed',
      'umrefPedido',
      'UmrefPedido',
      'umRefPedido',
    );
  }
  if (contexto === 'factura' || contexto === 'remito') {
    return readUmProp(
      p,
      'umrefFac',
      'UmrefFac',
      'umRefFac',
      'UmRefFac',
      'umrefFactura',
      'UmrefFactura',
    );
  }
  return '';
}

/**
 * Resuelve UM de documento + precio unitario en esa UM a partir del precio de lista.
 * `umPrecioLista`: UM a la que corresponde `precioLista` (ArticuloXListaPrecio.Um);
 * si vacío, se asume UM base del artículo.
 * Pedido: UM Ref. Pedido; si está vacía, unidad de medida base.
 * Factura y nota de crédito (alta sin documento anterior): UM Ref. Facturación;
 * si está vacía, unidad de medida base.
 * No toma equivalencias (eso mostraba Maple/MP y el stock en otra unidad).
 * La UM de un pedido o factura origen se conserva en la línea; este resolver no la pisa.
 * Remito: umrefFac → equivalencias → stock/base.
 */
export function resolverUmYPrecioDocumento(
  producto: unknown,
  precioLista: number,
  contexto: ContextoUmVenta,
  eqs: UmEquivalenciaDto[],
  umPrecioLista?: string | null,
): LineaUmPrecioResolucion {
  const umBase = umBaseProducto(producto);
  let um = umComercialExplicita(producto, contexto);
  if (contexto === 'pedido' || contexto === 'factura') {
    if (!um) um = umBase;
  } else {
    if (!um) um = umDesdeEquivalenciasSiCorresponde('', umBase, eqs);
    if (!um) um = umParaDocumento(producto, contexto) || umBase;
  }
  const umPrecio = normUm(umPrecioLista) || umBase;
  const precioUnitario = precioEnUmDocumento(precioLista, um, umBase, eqs, umPrecio);
  const factor = factorCantidadEntreUm(um, umPrecio, eqs, umBase);
  return {
    um,
    umBase,
    precioUnitario,
    usoEquivalencia: factor != null && Math.abs(factor - 1) > 1e-9,
  };
}

/** Incorpora UM de logística (pedido/factura/…) al producto para resolver la línea. */
export function aplicarUmLogisticaProducto(
  producto: unknown,
  detalle:
    | Pick<EquivalenciasUmDetalle, 'umrefPed' | 'umrefFac' | 'umrefPrep' | 'umrefStock' | 'umBase'>
    | null
    | undefined,
): Record<string, unknown> {
  const p = { ...((producto ?? {}) as Record<string, unknown>) };
  if (!detalle) return p;
  if (detalle.umrefPed) p.umrefPed = detalle.umrefPed;
  if (detalle.umrefFac) p.umrefFac = detalle.umrefFac;
  if (detalle.umrefPrep) p.umrefPrep = detalle.umrefPrep;
  if (detalle.umrefStock) p.umrefStock = detalle.umrefStock;
  if (detalle.umBase) {
    p.idUnidadMedida = detalle.umBase;
    if (!normUm(p.um as string)) p.um = detalle.umBase;
  }
  return p;
}

/**
 * Misma resolución que resolverUmYPrecioDocumento, usando el detalle de
 * equivalencias-um (equivalencias + tipo UM de logística).
 */
export function resolverUmYPrecioDesdeDetalle(
  producto: unknown,
  precioLista: number,
  contexto: ContextoUmVenta,
  detalle: EquivalenciasUmDetalle,
  umPrecioLista?: string | null,
): LineaUmPrecioResolucion {
  const productoUm = aplicarUmLogisticaProducto(producto, detalle);
  return resolverUmYPrecioDocumento(
    productoUm,
    precioLista,
    contexto,
    detalle.equivalencias ?? [],
    umPrecioLista,
  );
}

/** UM de inventario físico: preferir UM base (ahí suele estar el stock del depósito). */
export function umStockDesdeDetalle(
  detalle: Pick<EquivalenciasUmDetalle, 'umrefStock' | 'umBase'> | null | undefined,
  producto?: unknown,
): string {
  const base =
    normUm(detalle?.umBase) || (producto ? umBaseProducto(producto) : '');
  if (base) return base;
  const refStock = normUm(detalle?.umrefStock);
  if (refStock) return refStock;
  if (!producto) return '';
  const p = producto as Record<string, unknown>;
  return readUmProp(p, 'umrefStock', 'UmrefStock', 'umRefStock');
}

/**
 * Cantidad de la línea → cantidad en UM de stock.
 * Sin camino de equivalencia: devuelve la misma cantidad (comportamiento normal).
 */
export function cantidadHaciaUmStock(
  cantidad: number,
  umLinea: string,
  umStock: string,
  eqs: UmEquivalenciaDto[],
): number {
  const qty = Number(cantidad);
  if (!Number.isFinite(qty)) return 0;
  const from = normUm(umLinea);
  const to = normUm(umStock) || from;
  if (!from || !to || umKey(from) === umKey(to)) return qty;
  const conv = convertirCantidadEntreUm(qty, from, to, eqs, to);
  return conv == null ? qty : conv;
}

/**
 * Stock en UM de inventario → stock expresado en UM de la línea (para mostrar / comparar).
 * Sin conversión: devuelve el stock tal cual.
 */
export function stockEnUmDocumento(
  stockEnUmStock: number,
  umLinea: string,
  umStock: string,
  eqs: UmEquivalenciaDto[],
): number {
  const stock = Number(stockEnUmStock);
  if (!Number.isFinite(stock)) return stockEnUmStock;
  const to = normUm(umLinea);
  const from = normUm(umStock) || to;
  if (!from || !to || umKey(from) === umKey(to)) return stock;
  const conv = convertirCantidadEntreUm(stock, from, to, eqs, from);
  return conv == null ? stock : conv;
}

/**
 * Expresa el stock del depósito en la UM de la línea.
 * El stock de inventario siempre se asume en UM de stock/base (nunca en una UM
 * comercial de equivalencias). Si la línea ya está en esa UM, no convierte.
 * Solo convierte stockUm → umLinea cuando hay camino de equivalencia.
 */
export function resolverStockParaUmLinea(
  stockRaw: number,
  umLinea: string,
  detalle: EquivalenciasUmDetalle | null | undefined,
  producto?: unknown,
): { stock: number; convirtio: boolean; umStockUsada: string } {
  const umDoc = normUm(umLinea);
  const eqs = detalle?.equivalencias ?? [];
  const stock = Number(stockRaw);
  if (!umDoc || !Number.isFinite(stock)) {
    return { stock: stockRaw, convirtio: false, umStockUsada: '' };
  }

  // Orígenes válidos del stock físico: inventario/base. No usar UM de
  // equivalencias (p. ej. MP) como si el depósito estuviera cargado en ellas.
  const candidatos: string[] = [];
  const push = (u: string) => {
    const n = normUm(u);
    if (n && !candidatos.some((c) => umKey(c) === umKey(n))) candidatos.push(n);
  };
  push(umStockDesdeDetalle(detalle, producto));
  push(detalle?.umBase || '');
  push(detalle?.umrefStock || '');
  if (producto) push(umBaseProducto(producto));

  if (candidatos.length === 0) {
    return { stock, convirtio: false, umStockUsada: umDoc };
  }

  for (const umStock of candidatos) {
    if (umKey(umStock) === umKey(umDoc)) {
      return { stock, convirtio: false, umStockUsada: umStock };
    }
    const conv = convertirCantidadEntreUm(stock, umStock, umDoc, eqs, umStock);
    if (conv != null && Number.isFinite(conv)) {
      return { stock: conv, convirtio: true, umStockUsada: umStock };
    }
  }

  return { stock, convirtio: false, umStockUsada: candidatos[0] || umDoc };
}
